import { Link } from "@tanstack/react-router";
import { UserCog } from "lucide-react";
import { useState } from "react";
import { useAuditEvents } from "@/api/queries";
import type { AuditEvent, AuditFilters, ListQuery } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { EmptyState } from "@/components/feedback/empty-state";
import { Section } from "@/components/feedback/key-value";
import { QueryState } from "@/components/feedback/query-state";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OpportunityLink } from "@/components/navigation/entity-links";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/dates";
import { AUDIT_RESULT } from "@/lib/status";

const PAGE = 15;

/** A value as staff read it: money fields in dollars, times readable, objects as short JSON. */
function show(key: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number" && /cents$/i.test(key)) return `$${(value / 100).toLocaleString("en-US")}`;
  if (typeof value === "number") return value.toLocaleString("en-US");
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value);
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "yes" : "no";
  const json = JSON.stringify(value);
  return json.length > 120 ? `${json.slice(0, 120)}…` : json;
}

const label = (key: string) =>
  key
    .replace(/Cents$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[._]/g, " ")
    .toLowerCase();

/** What changed (before → after), or what was sent. */
function Changes({ e }: { e: AuditEvent }) {
  const after = e.after ?? {};
  const before = e.before ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  if (keys.length === 0) return null;
  return (
    <dl className="mt-1 space-y-0.5" data-testid="dealer-action-changes">
      {keys.map((k) => (
        <div key={k} className="flex flex-wrap gap-x-1.5">
          <dt className="text-muted-foreground">{label(k)}:</dt>
          <dd className="min-w-0 break-words">
            {k in before ? (
              <>
                <span className="text-muted-foreground line-through">{show(k, before[k])}</span> → {show(k, after[k])}
              </>
            ) : (
              show(k, after[k])
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What Rogue admins did in the dealer app on the dealership's behalf (audit `dealer_app.*`): who,
 * when, what (and on which lead), what changed or was sent, and whether it worked. Each row opens
 * the full audit event.
 */
export function DealerAppActions({
  filters,
  title = "Admin actions in the dealer app",
  showLead = false,
}: {
  filters: Pick<AuditFilters, "storeId" | "organizationId" | "resourceType" | "resourceId">;
  title?: string;
  /** Show which lead each action was on (store and organization views). */
  showLead?: boolean;
}) {
  const { can } = usePermissions();
  const [pageSize, setPageSize] = useState(PAGE);
  const query: ListQuery<AuditFilters> = { page: 1, pageSize, filters: { ...filters, action: "dealer_app.*", details: "true" } };
  const q = useAuditEvents(query);
  if (!can("audit:view")) return null;
  return (
    <Section title={`${title}${q.data ? ` (${q.data.total})` : ""}`} className="[&>div]:p-0">
      <QueryState query={q} data={q.data} resource="admin actions">
        {(page) =>
          page.items.length === 0 ? (
            <EmptyState icon={UserCog} title="No admin actions" description="Nothing was done here by a Rogue admin in the dealer app." />
          ) : (
            <>
              <ul className="divide-y text-xs" aria-label="Admin actions">
                {page.items.map((e) => {
                  const error = typeof e.metadata?.error === "string" ? e.metadata.error : null;
                  const what = e.action === "dealer_app.opened" ? "Opened the dealer app" : (e.resource.label ?? e.action.replace("dealer_app.", ""));
                  return (
                    <li key={e.id} className="px-3 py-2" data-testid="dealer-action">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium">{e.actor.name ?? "A Rogue admin"}</span>
                        <span>{what}</span>
                        {showLead && e.resource.type === "opportunity" ? <OpportunityLink id={e.resource.id} label="lead" /> : null}
                        <StatusBadge config={AUDIT_RESULT[e.result]} />
                        <span className="ml-auto text-muted-foreground">{formatDateTime(e.at)}</span>
                        <Link to="/audit/$eventId" params={{ eventId: e.id }} className="text-muted-foreground underline-offset-2 hover:underline">
                          Details
                        </Link>
                      </div>
                      <Changes e={e} />
                      {error ? <p className="mt-1 text-danger">{error}</p> : null}
                    </li>
                  );
                })}
              </ul>
              {page.total > page.items.length ? (
                <div className="border-t p-2 text-center">
                  <Button size="xs" variant="ghost" onClick={() => setPageSize((n) => n + PAGE)}>
                    Show more ({page.total - page.items.length})
                  </Button>
                </div>
              ) : null}
            </>
          )
        }
      </QueryState>
    </Section>
  );
}
